import { useActionState } from "react";
import { mergeTagAction } from "@/actions/tags";
import { Button } from "@/components/Button";
import { StaticCell } from "@/components/FormCell";
import styles from "@/components/FormRow.module.css";
import { LinkButton } from "@/components/LinkButton";
import { initialFormState } from "@/lib/formState";
import { useFormRedirect } from "@/lib/useFormRedirect";

const FORM_ID = "tag-merge";

type TagMergeRowProps = {
  // The listing owns the rows, so a save has to tell it to read again.
  onSaved: () => void;
  slug: string;
  templateCount: number;
  // Every other tag, so the survivor can only ever be one that exists.
  candidates: { slug: string; label: string }[];
};

export const TagMergeRow = ({
 onSaved,
  slug,
  templateCount,
  candidates,
}: TagMergeRowProps) => {
  const [state, formAction, isPending] = useActionState(
    mergeTagAction.bind(null, slug),
    initialFormState,
  );

  useFormRedirect(state, onSaved);

  return (
    <>
      <StaticCell value={slug} isRowHeader />
      <td className={styles.cell} colSpan={4}>
        <form id={FORM_ID} action={formAction} hidden />
        <label className={styles.mergeLabel} htmlFor="tag-merge-survivor">
          Merge into
        </label>
        <select
          id="tag-merge-survivor"
          form={FORM_ID}
          name="survivorSlug"
          className={styles.input}
          defaultValue=""
        >
          <option value="">— pick a tag —</option>
          {candidates.map((candidate) => (
            <option key={candidate.slug} value={candidate.slug}>
              {candidate.slug}
            </option>
          ))}
        </select>
        <p className={styles.mergeNote}>
          Every template carrying <strong>{slug}</strong> ({templateCount}) is
          rewritten to the tag you pick, and <strong>{slug}</strong> is deleted.
          Tags have no foreign key, so this one really does go — unlike
          everything else in the editor.
        </p>
        {state.fieldErrors.survivorSlug === undefined ? null : (
          <span className={styles.error} role="alert">
            {state.fieldErrors.survivorSlug.join(" ")}
          </span>
        )}
      </td>
      <td className={styles.actions}>
        <Button
          type="submit"
          form={FORM_ID}
          variant="danger"
          disabled={isPending}
        >
          {isPending ? "Merging..." : "Merge"}
        </Button>
        <LinkButton href="/tags" variant="secondary" shouldReplaceHistory>
          Cancel
        </LinkButton>
      </td>
    </>
  );
};
